import { useMemo, useState } from "react";
import { useList, useTranslate } from "@refinedev/core";
import { Button, DataTable, Field, Modal, StatusBadge, type Column } from "@fawkes/ui";

import { ErrorState, LoadingState } from "../../components/states";
import type { AdminUser } from "./types";
import { Detail } from "./DetailRow";

// Personen — a READ-ONLY list of PERSON users: people synced from Backoffice (a foster family is one
// such task; a person may carry several). The platform never originates a person and never edits one
// here — only Backoffice does, and the account is lazily provisioned on the person's first magic-code
// sign-in. So this surface has no create/edit/delete: it mirrors the per-type scoping of Serviceaccounts
// but exposes a plain list + read-only detail. Resource "admin/persons" → GET /api/admin/persons.

function userTypeLabel(t: (k: string) => string, userType: string): string {
  const key = userType.toLowerCase();
  return key === "account" || key === "person" || key === "service"
    ? t(`admin.users.type.${key}`)
    : t("admin.users.type.other");
}

export function Persons() {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useList<AdminUser>({
    resource: "admin/persons",
    pagination: { mode: "off" },
  });

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const rows = data?.data ?? [];
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...rows].sort((a, b) => a.username.localeCompare(b.username));
    if (q.length === 0) {
      return sorted;
    }
    return sorted.filter(
      (u) => u.username.toLowerCase().includes(q) || (u.email ?? "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  if (isLoading) {
    return <LoadingState />;
  }
  if (isError) {
    return <ErrorState onRetry={() => void refetch()} />;
  }

  const activeBadge = (active: boolean) => (
    <StatusBadge tone={active ? "success" : "neutral"}>
      {t(active ? "admin.common.active" : "admin.common.inactive")}
    </StatusBadge>
  );

  const columns: Column<AdminUser>[] = [
    { header: t("admin.users.column.username"), cell: (u) => u.username },
    { header: t("admin.users.column.firstName"), cell: (u) => u.firstName || "—" },
    { header: t("admin.users.column.lastName"), cell: (u) => u.lastName || "—" },
    { header: t("admin.users.column.email"), cell: (u) => u.email || "—" },
    { header: t("admin.users.column.active"), cell: (u) => activeBadge(u.active) },
    {
      header: t("admin.common.actions"),
      align: "right",
      cell: (u) => (
        <Button variant="secondary" onClick={() => setSelected(u)}>
          {t("admin.common.view")}
        </Button>
      ),
    },
  ];

  return (
    <div className="fawkes-admin-page">
      <p className="fawkes-portal__muted">{t("admin.persons.intro")}</p>

      <div className="fawkes-admin__toolbar">
        <Field
          label={t("admin.users.search.label")}
          placeholder={t("admin.users.search.placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowKey={(u) => u.id}
        empty={<span className="fawkes-portal__muted">{t("admin.persons.empty")}</span>}
      />

      <Modal
        open={selected !== null}
        title={selected?.username ?? ""}
        onClose={() => setSelected(null)}
        footer={
          <Button variant="secondary" onClick={() => setSelected(null)}>
            {t("admin.common.close")}
          </Button>
        }
      >
        {selected ? (
          <dl className="fawkes-admin__fields">
            <Detail label={t("admin.users.column.username")} value={selected.username} />
            <Detail label={t("admin.users.column.firstName")} value={selected.firstName} />
            <Detail label={t("admin.users.column.lastName")} value={selected.lastName} />
            <Detail label={t("admin.users.column.email")} value={selected.email} />
            <Detail label={t("admin.users.column.timeZone")} value={selected.timeZoneId} />
            <Detail label={t("admin.users.column.type")} value={userTypeLabel(t, selected.userType)} />
            <div className="fawkes-admin__field">
              <dt>{t("admin.users.column.active")}</dt>
              <dd>{activeBadge(selected.active)}</dd>
            </div>
          </dl>
        ) : null}
      </Modal>
    </div>
  );
}
